import { FileTreeNode, SharedFile } from "domain/fileSharing";

// For individual file and folder segment names — no path separators.
export const SAFE_FILENAME_PATTERN = /^[a-zA-Z0-9._-]+$/;
export const FOLDER_PLACEHOLDER_FILENAME = "__folderPlaceholder";
// For user-entered new folder paths which may contain slashes (e.g. "archive/2024").
const SAFE_PATH_PATTERN = /^[a-zA-Z0-9._\-/]+$/;

const FILE_TREE_NODE_SORT_KEYS = [
    "filename",
    "fileSize",
    "creationDate",
    "uploader",
    "editedBy",
    "editedDate",
] as const;
export type FileTreeNodeSortKey = (typeof FILE_TREE_NODE_SORT_KEYS)[number];

export function isFileTreeNodeSortKey(key: string): key is FileTreeNodeSortKey {
    return (FILE_TREE_NODE_SORT_KEYS as readonly string[]).includes(key);
}

interface FolderBucket {
    subFolders: Map<string, FolderBucket>;
    files: SharedFile[];
}

export function buildFileTree(
    files: SharedFile[],
    sortColumn?: FileTreeNodeSortKey,
    sortAscending = true
): FileTreeNode[] {
    const root: FolderBucket = { subFolders: new Map(), files: [] };
    const placeholderUuids = new Map<string, string>();

    for (const file of files) {
        const segments = file.directoryPath ? file.directoryPath.split("/").filter(Boolean) : [];
        let current = root;
        for (const segment of segments) {
            if (!current.subFolders.has(segment)) {
                current.subFolders.set(segment, { subFolders: new Map(), files: [] });
            }
            current = current.subFolders.get(segment)!;
        }
        if (file.filename === FOLDER_PLACEHOLDER_FILENAME) {
            placeholderUuids.set(file.directoryPath ?? "", file.uuid);
        } else {
            current.files.push(file);
        }
    }

    return flattenFolder(root, "", sortColumn, sortAscending, placeholderUuids);
}

function sumFolderSize(folder: FolderBucket): number {
    const directSize = folder.files.reduce((sum, file) => sum + file.fileSize, 0);
    const nestedSize = [...folder.subFolders.values()].reduce((sum, sub) => sum + sumFolderSize(sub), 0);
    return directSize + nestedSize;
}

// Columns where folders have no meaningful value and should sort last.
const FOLDER_NULL_COLUMNS = new Set<FileTreeNodeSortKey>(["creationDate", "uploader"]);

function flattenFolder(
    folder: FolderBucket,
    folderPath: string,
    sortColumn?: FileTreeNodeSortKey,
    sortAscending = true,
    placeholderUuids?: Map<string, string>
): FileTreeNode[] {
    const folderNodes: FileTreeNode[] = [...folder.subFolders.entries()].map(([name, subFolder]) => {
        const fullPath = folderPath ? `${folderPath}/${name}` : name;
        return {
            isFolder: true,
            folderPath: fullPath,
            uuid: placeholderUuids?.get(fullPath) ?? "",
            fileSize: sumFolderSize(subFolder),
            checksum: "",
            filename: name,
            creationDate: "",
            uploader: "",
            directoryPath: folderPath,
            children: flattenFolder(subFolder, fullPath, sortColumn, sortAscending, placeholderUuids),
        };
    });

    const fileNodes: FileTreeNode[] = folder.files.map((file) => ({ ...file, isFolder: false }));

    if (!sortColumn) {
        return [...folderNodes.sort((a, b) => a.filename.localeCompare(b.filename)), ...fileNodes];
    }

    return [...folderNodes, ...fileNodes].sort((a, b) => {
        const aValRaw = a[sortColumn];
        const bValRaw = b[sortColumn];
        const aVal = a.isFolder && FOLDER_NULL_COLUMNS.has(sortColumn) ? undefined : aValRaw;
        const bVal = b.isFolder && FOLDER_NULL_COLUMNS.has(sortColumn) ? undefined : bValRaw;

        if (aVal == null && bVal == null) {
            return 0;
        }
        if (aVal == null) {
            return sortAscending ? 1 : -1;
        }
        if (bVal == null) {
            return sortAscending ? -1 : 1;
        }

        const compare =
            typeof aVal === "number" && typeof bVal === "number"
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal));
        return sortAscending ? compare : -compare;
    });
}

export function collectFileUuids(node: FileTreeNode): string[] {
    if (!node.isFolder) {
        return [node.uuid];
    }
    const childUuids = node.children.flatMap(collectFileUuids);
    return node.uuid ? [node.uuid, ...childUuids] : childUuids;
}

export function collectFileLeafNodes(node: FileTreeNode): FileTreeNode[] {
    if (!node.isFolder) {
        return [node];
    }
    const childLeaves = node.children.flatMap(collectFileLeafNodes);
    return node.uuid
        ? [{ ...node, isFolder: false, filename: FOLDER_PLACEHOLDER_FILENAME }, ...childLeaves]
        : childLeaves;
}

export function formatBytes(bytes: number): string {
    if (bytes < 1_024) {
        return `${bytes} B`;
    }
    if (bytes < 1_024 * 1_024) {
        return `${(bytes / 1_024).toFixed(1)} KB`;
    }
    if (bytes < 1_024 * 1_024 * 1_024) {
        return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
    }
    return `${(bytes / (1_024 * 1_024 * 1_024)).toFixed(1)} GB`;
}

export function baseFilename(filename: string, directoryPath: string): string {
    const prefix = `${directoryPath}/`;
    return directoryPath && filename.startsWith(prefix) ? filename.slice(prefix.length) : filename;
}

export function computePath(selectedPath: string, newFolder: string): string {
    const folder = newFolder.trim().replace(/^\/+|\/+$/g, "");
    if (!folder) {
        return selectedPath;
    }
    return selectedPath ? `${selectedPath}/${folder}` : folder;
}

export function isValidFolderPath(newFolder: string): boolean {
    const folder = newFolder.trim().replace(/^\/+|\/+$/g, "");
    return !folder || SAFE_PATH_PATTERN.test(folder);
}
